import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  useTheme
} from '@mui/material';
import { 
  Security, 
  Info, 
  Storage, 
  Visibility,
  DeleteForever,
  Cookie,
  Update,
  ContactSupport
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getTranslation } from '../../i18n';
import { Link } from 'react-router-dom';

const PrivacyPage = () => {
  const { language } = useSelector((state) => state.ui);
  const translations = getTranslation(language);
  const theme = useTheme();

  // Privacy policy sections
  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      icon: <Info color="primary" />,
      content: `This Privacy Policy explains how SkyRadar ("we," "us," or "our") collects, uses, and discloses your information when you use our website, mobile application, and services (collectively, the "Services"). We are committed to protecting your privacy and ensuring the security of your personal information. By accessing or using our Services, you agree to the terms of this Privacy Policy.`
    },
    {
      id: 'information-collected',
      title: 'Information We Collect',
      icon: <Storage color="primary" />,
      content: `We collect several types of information from and about users of our Services, including:

• Personal Information: Name, email address, postal address, phone number, and other identifiers that you provide when registering for our Services or contacting us.

• Usage Data: Information about how you use our Services, including IP address, browser type, device information, pages visited, and time spent on our Services.

• Location Data: With your consent, we may collect precise location data to provide location-based services, such as lightning alerts for your area.

• Communication Data: Records of your communications with us, including support requests, feedback, and survey responses.`
    },
    {
      id: 'information-use',
      title: 'How We Use Your Information',
      icon: <Visibility color="primary" />,
      content: `We use the information we collect for various purposes, including:

• Providing and improving our Services, including personalized lightning alerts and weather information.

• Communicating with you about your account, updates to our Services, and responding to your inquiries.

• Analyzing usage patterns to enhance user experience and develop new features.

• Ensuring the security and integrity of our Services.

• Marketing our Services to you, subject to your preferences and applicable laws.`
    },
    {
      id: 'information-sharing',
      title: 'Information Sharing and Disclosure',
      icon: <Security color="primary" />,
      content: `We may share your information in the following circumstances:

• With service providers who perform services on our behalf, such as hosting, data analysis, and customer service.

• With business partners who offer services that may be of interest to you, but only with your consent.

• In response to legal process or when we believe disclosure is necessary to protect our rights, property, or safety, or that of our users or others.

• In connection with a business transaction, such as a merger, acquisition, or sale of assets.

We do not sell your personal information to third parties.`
    },
    {
      id: 'cookies',
      title: 'Cookies and Similar Technologies',
      icon: <Cookie color="primary" />,
      content: `We use cookies and similar technologies to collect information about your browsing activities and to distinguish you from other users of our Services. Cookies help us provide you with a better experience by enabling us to:

• Remember your preferences and settings.

• Understand how you use our Services.

• Enhance the security of our Services.

• Measure the effectiveness of our marketing campaigns.

You can control cookies through your browser settings and other tools. However, if you block certain cookies, you may not be able to use all the features of our Services.`
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: <Security color="primary" />,
      content: `We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, disclosure, alteration, and destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.`
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      icon: <DeleteForever color="primary" />,
      content: `We retain your personal information for as long as necessary to fulfill the purposes for which we collected it, including for the purposes of satisfying any legal, accounting, or reporting requirements. To determine the appropriate retention period, we consider the amount, nature, and sensitivity of the personal information, the potential risk of harm from unauthorized use or disclosure, and applicable legal requirements.`
    },
    {
      id: 'your-rights',
      title: 'Your Rights and Choices',
      icon: <ContactSupport color="primary" />,
      content: `Depending on your location, you may have certain rights regarding your personal information, including:

• Access: You can request access to your personal information.

• Correction: You can request that we correct inaccurate or incomplete information.

• Deletion: You can request that we delete your personal information in certain circumstances.

• Restriction: You can request that we restrict the processing of your personal information.

• Data Portability: You can request a copy of your personal information in a structured, commonly used, and machine-readable format.

• Objection: You can object to our processing of your personal information in certain circumstances.

To exercise these rights, please contact us using the information provided in the "Contact Us" section below.`
    },
    {
      id: 'policy-updates',
      title: 'Changes to This Privacy Policy',
      icon: <Update color="primary" />,
      content: `We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically.`
    },
    {
      id: 'contact',
      title: 'Contact Us',
      icon: <ContactSupport color="primary" />,
      content: `If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:

Email: privacy@skyradar.com

We will respond to your inquiry as soon as reasonably possible.`
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Last Updated: June 1, 2023
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 800, mx: 'auto', mt: 2 }}>
          At SkyRadar, we value your privacy and are committed to protecting your personal information.
          This Privacy Policy explains how we collect, use, and safeguard your data when you use our services.
        </Typography>
      </Box>

      {/* Table of Contents */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Table of Contents
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          {sections.map((section) => (
            <Button 
              key={section.id}
              variant="outlined" 
              size="small"
              href={`#${section.id}`}
              sx={{ 
                borderRadius: 4,
                textTransform: 'none',
                px: 2
              }}
            >
              {section.title}
            </Button>
          ))}
        </Box>
      </Paper>

      {/* Policy Sections */}
      {sections.map((section) => (
        <Box key={section.id} id={section.id} sx={{ mb: 5, scrollMarginTop: '100px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {section.icon}
            <Typography variant="h5" component="h2" sx={{ ml: 2 }}>
              {section.title}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {section.content}
          </Typography>
        </Box>
      ))}

      {/* Footer */}
      <Box sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          If you have any questions about this Privacy Policy, please <Link to="/contact">contact us</Link>.
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
          {translations.landing.footer.email}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          component={Link}
          to="/contact"
          sx={{ mt: 2 }}
        >
          Contact Us
        </Button>
      </Box>
    </Container>
  );
};

export default PrivacyPage;
